import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { categoryToRiskLevel, impactLevelInfo, riskLevels, } from "../../../app/riskLevel";
import {
    ADJACENT_WAYPOINT_ERROR,
    DESIRED_END_TIME_ERROR_TEXT,
    FINAL_END_TIME_ERROR_TEXT,
    LEVEL_TWO,
    LOCATION_INVALID
} from "../../UserVisibleStrings";
import {
    DateMessage,
    RouteRequestInput,
    RouteRequestInputType,
    routeRequestInputTypeFromJSON,
    TimeOfDay,
    Trip,
    TripRequest,
    Waypoint,
} from "../../../generated/gpb/api/route";
import { RiskCategory, riskCategoryFromJSON } from "../../../generated/gpb/api/risk";
import { WaypointExpanded } from "../../../features/riskViewer/riskViewerSlice";
import { ConfirmType } from "./ConfirmEditTypeDialog";
import { TripId } from "../../../generated/gpb/api/id";

export const RouteRequestInputTypeStrings = [
    "UNRECOGNIZED",
    "SUREROUTE",
    "ADVISORY",
    "MULTISTART"
] as const;

const confirmTypeSchema = z.nativeEnum( ConfirmType );

export const RouteRequestInputStringToType = {
    "UNRECOGNIZED": RouteRequestInputType.UNRECOGNIZED,
    "SUREROUTE": RouteRequestInputType.SUREROUTE,
    "ADVISORY": RouteRequestInputType.ADVISORY,
    "MULTISTART": RouteRequestInputType.MULTISTART,
} as const;

export function routeRequestInputTypeToString( type: RouteRequestInputType ): "SUREROUTE" | "ADVISORY" | "MULTISTART"
{
    switch ( type )
    {
        case RouteRequestInputType.SUREROUTE:
            return "SUREROUTE";
        case RouteRequestInputType.ADVISORY:
            return "ADVISORY";
        case RouteRequestInputType.MULTISTART:
            return "MULTISTART";
        default:
            throw new Error( `Unknown RouteRequestInputType: ${type}` );
    }
}


export const formLocationSchema = z.object( {
    locationName: z.string().min( 1, "Valid street address or city required" ),
    location: z
        .object( {
            longitude: z.number(),
            latitude: z.number(),
        } )
        .required(),
    valid: z.boolean(),
} )
    .refine( ( data ) => data.valid, {
        message: LOCATION_INVALID,
        path: [ "locationName" ],
    } );

const formWaypointSchema = z.object( {
    location: formLocationSchema,
    // Oneof representation using union

    overnightStop: z.object( {
        minNights: z
            .number( { invalid_type_error: "Overnights must be a number" } )
            .min( 1, "Minimum nights must be at least 1" )
            .max( 30, "Maximum nights cannot exceed 30" ),
        maxNights: z
            .number( { invalid_type_error: "Overnights must be a number" } )
            .min( 1, "Minimum nights must be at least 1" )
            .max( 30, "Maximum nights cannot exceed 30" ),
    } ).refine( data => data.maxNights >= data.minNights, {
        message: "Max nights cannot be less than min nights",
        path: [ "maxNights" ],
    } ).optional(),
    pitStop: z.object( {
        minDurationMinutes: z
            .number( { invalid_type_error: "Duration must be a number" } )
            .min( 0, "Minimum duration must be at least 0" ),
        maxDurationMinutes: z
            .number( { invalid_type_error: "Duration must be a number" } )
            .min( 0, "Minimum duration must be at least 0" ),
    } ).optional(),
    startTime: z.object( {
        hour: z
            .number( { invalid_type_error: "Hour must be a number" } )
            .min( 0, "Hour must be between 0 and 24" )
            .max( 24, "Hour must be between 0 and 24" ),
        minute: z
            .number( { invalid_type_error: "Minute must be a number" } )
            .min( 0, "Minute must be between 0 and 59" )
            .max( 59, "Minute must be between 0 and 59" ),
    } ).optional(),
    firstDayDesiredEndOverride: z.object( {
        hour: z
            .number( { invalid_type_error: "Hour must be a number" } )
            .min( 0, "Hour must be between 0 and 24" )
            .max( 24, "Hour must be between 0 and 24" ),
        minute: z
            .number( { invalid_type_error: "Minute must be a number" } )
            .min( 0, "Minute must be between 0 and 59" )
            .max( 59, "Minute must be between 0 and 59" ),
    } ).optional(),
} );

//export type FormWaypoint = z.infer<typeof formWaypointSchema>;

export type FormLocation = z.infer<typeof formLocationSchema>;

export const formDateSchema = z.object( {
    month: z.number(),
    day: z.number(),
    year: z.number(),
} );

export type FormDate = z.infer<typeof formDateSchema>;
export type FormWaypoint = z.infer<typeof formWaypointSchema>;
const routeRequestFormSchema = z.object( {
    isAdvisory: z.boolean(),
    name: z.string().trim().min( 1, { message: "Name is required" } ).max( 256 ),
    startDate: formDateSchema,
    waypoints: z.array( formWaypointSchema ).min( 2 ),
    dailyStartTime: z.date(),
    dailyDesiredEndTime: z.date(),
    dailyFinalEndTime: z.date(),
    maxDrivingSpeed: z.number(),
    overrideMaxSpeed: z.boolean(),
    maxRiskLevel: z.enum( riskLevels ),
    avoidTolls: z.boolean(),
    requestType: z.enum( RouteRequestInputTypeStrings ),
    weightPounds: z
        .union( [ z.number().min( 0 ), z.undefined() ] )
        .refine( val => val === undefined || val >= 0, {
            message: "Input must be undefined or a number >= 0",
        } ),
    submitType: confirmTypeSchema,
    heightFeet: z
        .number()
        .int()
        .min( 0 )
        .max( 14 )
        .optional(),
    heightInches: z
        .number()
        .int()
        .min( 0 )
        .max( 12 )
        .optional( ),
    includeUS: z.boolean(),
    includeCanada: z.boolean(),
    includeMexico: z.boolean(),
} )
    .superRefine( ( object, ctx ) =>
    {
        for ( let i = 0; i < object.waypoints.length - 1; i++ )
        {
            if ( object.waypoints[i].location.locationName === object.waypoints[i + 1].location.locationName )
            {
                ctx.addIssue( {
                    code: z.ZodIssueCode.custom,
                    message: ADJACENT_WAYPOINT_ERROR,
                    path: [ "waypoints", i + 1, "location", "locationName" ],
                } );
            }
        }

        if ( !object.isAdvisory )
        {
            const heightError = heightInputValidator( object.heightFeet, object.heightInches );

            if ( heightError !== undefined )
            {
                ctx.addIssue( {
                    code: z.ZodIssueCode.custom,
                    message: heightError,
                    path: [ "heightFeet" ],
                } );
            }

            const desiredEndTimeError = dailyDesiredEndTimeValidator( object.dailyStartTime, object.dailyDesiredEndTime );

            if ( desiredEndTimeError !== undefined )
            {
                ctx.addIssue( {
                    code: z.ZodIssueCode.custom,
                    message: desiredEndTimeError,
                    path: [ "dailyDesiredEndTime" ],
                } );
            }

            const finalEndTimeError = dailyFinalEndTimeValidator( object.dailyDesiredEndTime, object.dailyFinalEndTime );

            if ( finalEndTimeError !== undefined )
            {
                ctx.addIssue( {
                    code: z.ZodIssueCode.custom,
                    message: finalEndTimeError,
                    path: [ "dailyFinalEndTime" ],
                } );
            }
        }
    } );

export type RouteRequestFormData = z.infer<typeof routeRequestFormSchema>;

export const clientResolver = zodResolver( routeRequestFormSchema );

export function formToTripRequest( form: RouteRequestFormData, isAdvisory: boolean, parentTripId: TripId | undefined ): TripRequest
{
    return {
        tripName: form.name,
        routeRequestInput: formToRouteRequestInput( form, isAdvisory ),
        parentTripId: parentTripId
    };
}
export function stripWaypointsOfValidField( form: RouteRequestFormData ): Array<Waypoint>
{
    return form.waypoints.map( each =>
    {
        return Waypoint.fromJSON( each );
    } );
}
export type CountryAssessment =
{
    includeCanada: boolean,
    includeMexico: boolean,
    includeUs: boolean,
};
const countries = [ "United States", "Canada", "Mexico" ];
export function getCountryName( locationName: string | undefined )
{
    if ( locationName === undefined ) return "";
    const parts = locationName.split( "," );
    if ( parts.length === 0 ) return "United States";
    const country = parts[parts.length - 1];
    if ( countries.includes( country ) ) return country;
    return "United States";
}
export function assessCountries( waypoints: Array<FormWaypoint>, overrideIncludeUs: boolean, overrideIncludeMexico: boolean, overrideIncludeCanada: boolean, ): CountryAssessment
{
    let includeCanada = overrideIncludeCanada;
    let includeMexico = overrideIncludeMexico;
    let includeUs = overrideIncludeUs;
    let hasAlaskaWaypoint = false;
    let hasNonAlaskaWaypoint = false;
    // Support multiple possible formats including:
    // --- Talkeetna, Alaska 99676-1234, United States
    // --- Talkeetna, Alaska 99676, United States
    // --- Talkeetna, Alaska 99676-1234
    // --- Talkeetna, Alaska 99676
    // --- Talkeetna, Alaska
    const alaskaRegex = new RegExp( ", Alaska(?: \\d{5}(?:-\\d{4})?)?(, United States)?$", "i" );
    waypoints.forEach( ( each: FormWaypoint ) =>
    {
        // If the location is inside the United States, this may only return the state or state + zip code
        const country: string = each.location.locationName.split( "," ).at( -1 )?.trim() ?? "";
        if ( country === "Canada" ) includeCanada = true;
        else if ( country === "Mexico" ) includeMexico = true;
        else if ( country === "" ) return false;
        else includeUs = true;

        // Check the full location name to validate for Alaska routes
        if ( alaskaRegex.test( each.location.locationName ) ) hasAlaskaWaypoint = true;
        else hasNonAlaskaWaypoint = true;
    } );
    if ( includeCanada && includeMexico )
    {
        includeUs = true;
    }
    if ( hasAlaskaWaypoint && hasNonAlaskaWaypoint )
    {
        includeCanada = true;
    }
    return {
        includeCanada,
        includeMexico,
        includeUs,
    } as CountryAssessment;
}
export function formToRouteRequestInput(
    form: RouteRequestFormData,
    isAdvisory: boolean,
): RouteRequestInput
{
    const countryAssessment: CountryAssessment = assessCountries( form.waypoints, form.includeUS, form.includeMexico, form.includeCanada );
    return {
        startDate: {
            year: form.startDate.year,
            month: form.startDate.month,
            day: form.startDate.day,
        },
        waypoints: stripWaypointsOfValidField( form ),
        // Local time, router is time zone aware and will correct for this
        desiredStartTimeOfDay: {
            hour: form.dailyStartTime.getHours(),
            minute: form.dailyStartTime.getMinutes(),
        },

        desiredEndTimeOfDay: isAdvisory ? undefined : {
            hour: form.dailyDesiredEndTime.getHours(),
            minute: form.dailyDesiredEndTime.getMinutes(),
        },

        finalEndTimeOfDay: isAdvisory ? undefined : {
            hour: form.dailyFinalEndTime.getHours(),
            minute: form.dailyFinalEndTime.getMinutes()
        },
        maxSpeed: form.overrideMaxSpeed ? form.maxDrivingSpeed : ABSOLUTE_MAX_SPEED, // Send the default max so max speed is always defined (even if not overridden
        acceptableRiskLevelCeiling: isAdvisory ? RiskCategory.RISK_CATEGORY_LOW : impactLevelInfo[form.maxRiskLevel].category,
        maxWeightLbs: form.weightPounds,
        maxHeightFt: form.heightFeet !== undefined && form.heightInches !== undefined
            ? form.heightFeet + ( form.heightInches / 12 )
            : undefined,
        submissionTime: new Date(),
        avoidTolls: form.avoidTolls,
        requestType: isAdvisory ? RouteRequestInputType.ADVISORY : RouteRequestInputType.SUREROUTE,
        variationTag: "",
        includeCanada: countryAssessment.includeCanada,
        includeMexico: countryAssessment.includeMexico,
        includeUs: countryAssessment.includeUs
    };
}

export function generateMultistartRouteRequestInput(
    routeRequestInput: RouteRequestInput
): RouteRequestInput
{
    return {
        startDate: routeRequestInput.startDate,
        waypoints: routeRequestInput.waypoints,
        // Local time, router is time zone aware and will correct for this
        desiredStartTimeOfDay: routeRequestInput.desiredStartTimeOfDay,
        desiredEndTimeOfDay: routeRequestInput.desiredEndTimeOfDay,
        finalEndTimeOfDay: routeRequestInput.finalEndTimeOfDay,
        maxSpeed: routeRequestInput.maxSpeed, // Send the default max so max speed is always defined (even if not overridden
        acceptableRiskLevelCeiling: routeRequestInput.acceptableRiskLevelCeiling,
        maxWeightLbs: routeRequestInput.maxWeightLbs,
        maxHeightFt: routeRequestInput.maxHeightFt,
        submissionTime: new Date(),
        avoidTolls: routeRequestInput.avoidTolls,
        requestType: RouteRequestInputType.MULTISTART,
        variationTag: routeRequestInput.variationTag,
        includeCanada: routeRequestInput.includeCanada,
        includeMexico: routeRequestInput.includeMexico,
        includeUs: routeRequestInput.includeUs
    };
}

export function resubmitRouteRequestInput(
    oldInput: RouteRequestInput
): RouteRequestInput
{
    return {
        startDate: oldInput.startDate,
        waypoints: oldInput.waypoints,
        desiredStartTimeOfDay: oldInput.desiredStartTimeOfDay,
        desiredEndTimeOfDay: oldInput.desiredEndTimeOfDay,
        finalEndTimeOfDay: oldInput.finalEndTimeOfDay,
        maxSpeed: oldInput.maxSpeed,
        acceptableRiskLevelCeiling: oldInput.acceptableRiskLevelCeiling,
        maxWeightLbs: oldInput.maxWeightLbs,
        maxHeightFt: oldInput.maxHeightFt,
        submissionTime: new Date(),
        avoidTolls: oldInput.avoidTolls,
        variationTag: "",
        requestType: oldInput.requestType,
        includeCanada: oldInput.includeCanada,
        includeMexico: oldInput.includeMexico,
        includeUs: oldInput.includeUs
    };
}


export const ABSOLUTE_MAX_SPEED = 85; // Drivers will follow posted speed limits, this is just an upper limit
export const DEFAULT_MAX_SPEED = 65; // Use this value to init the slider (when first enabled by the override checkbox)

export function tripToForm( trip: Trip, routeRequestInput: RouteRequestInput ): RouteRequestFormData
{
    //automatically set the start date to the current time if it is in the past.
    let startDate = dateMessageToDate( routeRequestInput?.startDate );
    if ( startDate.getTime() < Date.now() )
    {
        const d = new Date();
        zeroOutDate( d );
        startDate = d;
    }

    // TODO: fix the defaults in the method
    return {
        isAdvisory: routeRequestInput.requestType === RouteRequestInputType.ADVISORY,
        name: trip.name,
        startDate: dateToSchema( startDate ),
        waypoints: convertWaypoints( routeRequestInput?.waypoints ) ?? [],
        dailyStartTime: ( () =>
        {
            const date = new Date();
            date.setMinutes( routeRequestInput?.desiredStartTimeOfDay?.minute ?? 0 );
            date.setHours( routeRequestInput?.desiredStartTimeOfDay?.hour ?? 0 );
            return date;
        } )(),
        dailyDesiredEndTime: ( () =>
        {
            const date = new Date();
            date.setMinutes( routeRequestInput?.desiredEndTimeOfDay?.minute ?? 0 );
            date.setHours( routeRequestInput?.desiredEndTimeOfDay?.hour ?? 17 );
            return date;
        } )(),
        dailyFinalEndTime: ( () =>
        {
            const date = new Date();
            date.setMinutes( routeRequestInput?.finalEndTimeOfDay?.minute ?? 0 );
            date.setHours( ( routeRequestInput?.finalEndTimeOfDay?.hour ?? 17 ) );
            return date;
        } )(),
        maxDrivingSpeed:
      routeRequestInput?.maxSpeed === undefined ? ABSOLUTE_MAX_SPEED : routeRequestInput.maxSpeed,
        overrideMaxSpeed: routeRequestInput.maxSpeed !== undefined &&
        routeRequestInput.maxSpeed !== null &&
        routeRequestInput.maxSpeed < ABSOLUTE_MAX_SPEED,
        maxRiskLevel:
      routeRequestInput?.acceptableRiskLevelCeiling !== undefined
          ? categoryToRiskLevel( typeof routeRequestInput?.acceptableRiskLevelCeiling === "number" ? routeRequestInput?.acceptableRiskLevelCeiling : riskCategoryFromJSON( routeRequestInput?.acceptableRiskLevelCeiling ) )
          : LEVEL_TWO,
        avoidTolls: routeRequestInput?.avoidTolls === undefined ? false : routeRequestInput.avoidTolls,
        weightPounds: routeRequestInput?.maxWeightLbs,
        heightFeet: routeRequestInput?.maxHeightFt === undefined ? undefined : Math.floor( routeRequestInput?.maxHeightFt ),
        heightInches: routeRequestInput?.maxHeightFt === undefined ? undefined : Math.round( ( routeRequestInput?.maxHeightFt - Math.floor( routeRequestInput?.maxHeightFt ) ) * 12 ),
        requestType: routeRequestInput?.requestType === undefined ? RouteRequestInputTypeStrings[1]
            : routeRequestInputTypeToString( typeof routeRequestInput?.requestType === "number" ? routeRequestInput.requestType : routeRequestInputTypeFromJSON( routeRequestInput?.requestType ) ),
        submitType: ConfirmType.EDIT,
        includeUS: routeRequestInput.includeUs,
        includeMexico: routeRequestInput.includeMexico,
        includeCanada: routeRequestInput.includeCanada,
    };
}

export function zeroOutDate( d: Date )
{
    d.setHours( 0, 0, 0, 0 );
}
export function dateMessageToDate( msg: DateMessage | undefined, time?: TimeOfDay ): Date
{
    if ( msg === undefined )
    {
        const d = new Date();
        zeroOutDate( d );
        return d;
    }
    const { year, month, day } = msg;
    const hour = time?.hour ?? 0;
    const minute = time?.minute ?? 0;

    return new Date( year, month - 1, day, hour, minute, 0, 0 );
}
export function dateSchemaToDate( msg: FormDate, hour: number | undefined = undefined, minute: number | undefined = undefined ): Date
{
    if ( msg === undefined )
    {
        const date = new Date();
        zeroOutDate( date );
        if ( hour !== undefined )
        {
            date.setHours( hour );
        }
        if ( minute !== undefined )
        {
            date.setMinutes( minute );
        }
        return date;
    }
    return new Date( msg.year, msg.month - 1, msg.day, hour ?? 0, minute ?? 0, 0, 0 );
}
export function dateToSchema( msg: Date ): FormDate
{
    return {
        year: msg.getFullYear(),
        month: msg.getMonth() + 1,
        day: msg.getDate(),
    };
}

export const defaultWaypoint: WaypointExpanded = {
    location: {
        locationName: "",
        location: {
            longitude: 0,
            latitude: 0,
        },
        valid: false,
    },
    overnightStop: {
        minNights: 1,
        maxNights: 1,
    },
    pitStop: undefined,
    startTime: undefined,
    firstDayAfterWaypointDesiredEndOverride: undefined,
};


/**
 * Creates a new waypoint object from the provided waypoint data.
 * Ensures latitude, longitude, and locationName are defined.
 * Preserves stop information if indices are valid.
 * @param {Waypoint} waypoint - The original waypoint data.
 * @param {boolean} preserveStops - Whether to preserve stop information.
 * @returns {Waypoint | undefined} - The new waypoint object or undefined if data is invalid.
 */
export const createNewWaypoint = ( waypoint: WaypointExpanded, preserveStops: boolean ): WaypointExpanded =>
{

    if ( preserveStops )
    {
        // Check if overnightStop or pitStop is present and assign accordingly
        if ( waypoint.overnightStop )
        {
            waypoint.overnightStop = {
                minNights: waypoint.overnightStop.minNights,
                maxNights: waypoint.overnightStop.maxNights,
            };
        }
        if ( waypoint.pitStop )
        {
            waypoint.pitStop = {
                minDurationMinutes: waypoint.pitStop.minDurationMinutes,
                maxDurationMinutes: waypoint.pitStop.maxDurationMinutes,
            };
        }
    }
    else
    {
        waypoint.overnightStop = {
            minNights: 1,
            maxNights: 1,
        };
    }

    return waypoint;
};

export const heightInputValidator = (
    heightFeet?: number,
    heightInches?: number
) =>
{
    const bothDefined = heightFeet !== undefined && heightInches !== undefined;
    const bothUndefined = heightFeet === undefined && heightInches === undefined;

    if ( !bothDefined && !bothUndefined )
    {
        return "Height must be defined in feet and inches";
    }

    if ( bothDefined && ( heightFeet > 14 || ( heightFeet === 14 && heightInches > 0 ) ) )
    {
        return "Please specify a vehicle height less than 14 feet 0 inches";
    }

    return undefined;
};

export const dailyDesiredEndTimeValidator = ( startTime: Date, idealEndTime: Date ) =>
{
    if ( startTime >= idealEndTime )
    {
        return DESIRED_END_TIME_ERROR_TEXT;
    }
    return undefined;
};

export const dailyFinalEndTimeValidator = ( idealEndTime: Date, mustEndTime: Date ) =>
{
    if ( idealEndTime > mustEndTime )
    {
        return FINAL_END_TIME_ERROR_TEXT;
    }
    return undefined;
};

/**
 * I don't love that this is required, but we currently have a zod rule that requires overnight stays to be min 1, but
 * the backend wants the first and last waypoint to have a stay length of 0.  So we manually convert it back and forth
 * based on whether we're writing to the backend or loading from the backend.
 * @param waypoints
 */
export function convertWaypoints( waypoints: Array<Waypoint | WaypointExpanded> ): Array<WaypointExpanded>
{
    if ( waypoints === undefined || waypoints.length === 0 ) return [];
    return waypoints.map( ( waypoint, index ) =>
    {
        // Custom handling for first and last waypoints. The database has these values stored as 0, but validation
        // expects a value >= 1
        const isFirstOrLast = index === 0 || index === waypoints.length - 1;

        const result: {
            location: {
                locationName: string,
                location: {
                    longitude: number,
                    latitude: number,
                },
                valid: boolean,
            },
            overnightStop?: {
                minNights: number,
                maxNights: number,
            },
            pitStop?: {
                minDurationMinutes: number,
                maxDurationMinutes: number,
            },
            startTime: TimeOfDay | undefined,
            firstDayAfterWaypointDesiredEndOverride: TimeOfDay | undefined,
        } = {
            location: {
                locationName: waypoint.location?.locationName ?? "",
                location: {
                    longitude: waypoint.location?.location?.longitude === undefined ? 0 : waypoint.location?.location.longitude,
                    latitude: waypoint.location?.location?.latitude === undefined ? 0 : waypoint.location?.location?.latitude,
                },
                valid: true,
            },
            startTime: undefined,
            firstDayAfterWaypointDesiredEndOverride: undefined,
        };

        if ( isFirstOrLast )
        {
            result.overnightStop = {
                minNights: 1,
                maxNights: 1,
            };
            if ( waypoint.startTime !== undefined )
            {
                result.startTime = {
                    hour: waypoint.startTime.hour,
                    minute: waypoint.startTime.minute,
                };
            }
            return result;
        }

        // Include overnightStop only if it exists in the waypoint
        if ( waypoint.overnightStop !== undefined && waypoint.overnightStop.minNights !== undefined && waypoint.overnightStop.maxNights !== undefined )
        {
            result.overnightStop = {
                minNights: ( waypoint.overnightStop.minNights > 0 && waypoint.overnightStop.minNights <= 60 ) ? waypoint.overnightStop.minNights : 1,
                maxNights: ( waypoint.overnightStop.maxNights > 0 && waypoint.overnightStop.maxNights <= 60 ) ? waypoint.overnightStop.maxNights : 1,
            };
        }
        // Include pitStop only if it exists in the waypoint
        else if ( waypoint.pitStop !== undefined && waypoint.pitStop.minDurationMinutes !== undefined && waypoint.pitStop.maxDurationMinutes !== undefined )
        {
            result.pitStop = {
                minDurationMinutes: waypoint.pitStop.minDurationMinutes,
                maxDurationMinutes: waypoint.pitStop.maxDurationMinutes,
            };
        }
        // otherwise, default to overnight stop of one night
        else
        {
            result.overnightStop = {
                minNights: 1,
                maxNights: 1,
            };
        }

        return result;

    } );
}

